import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

// Views
import Image from '../../Image'

// Shared Styled Elements
import { Wrapper, Label } from '../SharedStyles.element'

// Styled Elements
import {
  SelectWrapper,
  SelectInput,
  SelectInputLabel,
  SelectInputOptionsContainer,
  SelectInputOptions,
  SelectPlaceholder,
  Abbreviation,
  Text,
} from './Select.elements'

// Function that listens to oustide clicks of given ref
const useOutsideAlerter = (ref, setShowOptions) => {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowOptions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref])
}

function Select(props) {
  // Destructure
  const { id, label, placeholder, options, onChange, value, disabled, ...rest } = props

  // Variables
  const withOptions = options && options?.length > 0
  const wrapperRef = useRef()

  // States
  const [formValue, setFormValue] = useState('')
  const [valueLabel, setValueLabel] = useState('')
  const [showOptions, setShowOptions] = useState(false)

  // Functions
  const handleOnChange = (value, label) => {
    onChange(value)
    if (value === undefined) {
      setFormValue(value)
      setValueLabel(placeholder || '')
      return setShowOptions(false)
    }
    setFormValue(value)
    setValueLabel(label)
    setShowOptions(false)
  }
  // Closing options on click away
  useOutsideAlerter(wrapperRef, setShowOptions)

  // UseEffects
  useEffect(() => {
    // setting the value and label on initial load
    if (value !== undefined && value !== null) {
      setFormValue(value)
      const currentLabel = options.find((data) => {
        if (typeof data.value === 'number') {
          return data.value === Number(value)
        }
        return data.value === value
      })
      if (currentLabel?.label) {
        setValueLabel(currentLabel.label)
      } else if (!currentLabel?.label) {
        setValueLabel(placeholder || '')
      }
    }
  }, [value])

  return (
    <SelectWrapper ref={wrapperRef} label={label}>
      {label && <Label disabled={disabled}>{label}</Label>}
      <Wrapper>
        <SelectInput id={id} type="select" value={`${formValue}`} onChange={() => {}} {...rest} />
        <SelectInputLabel
          open={showOptions}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setShowOptions((prevState) => !prevState)
            }
          }}
        >
          <SelectPlaceholder disabled={disabled}> {!valueLabel ? placeholder : valueLabel} </SelectPlaceholder>
        </SelectInputLabel>
        {showOptions && (
          <SelectInputOptionsContainer open={showOptions}>
            {withOptions &&
              options.map(({ value, label, disabled, abrv, icon }) => (
                <div key={label}>
                  {label !== valueLabel && (
                    <SelectInputOptions
                      disabled={disabled}
                      onClick={() => {
                        if (!disabled) handleOnChange(value, label)
                      }}
                      value={value}
                    >
                      {icon && <Image src={icon} width={30} height={30} />}
                      <Text>{label}</Text>
                      {abrv && <Abbreviation> {abrv} </Abbreviation>}
                    </SelectInputOptions>
                  )}
                </div>
              ))}
          </SelectInputOptionsContainer>
        )}
      </Wrapper>
    </SelectWrapper>
  )
}

// Default Props
Select.defaultProps = {
  id: '',
  label: '',
  value: '',
  placeholder: '',
  options: [],
  onChange: () => {},
  disabled: false,
}

// Proptypes Validation
Select.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  placeholder: PropTypes.string,
  options: PropTypes.instanceOf(Array),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
}

export default Select
